import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

export default function AdminAnnouncements() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '', content: '', priority: 'normal', target_audience: 'all', is_published: true,
  });

  useEffect(() => {
    api.get('/admin/announcements/').then(res => setAnnouncements(res.data.results || res.data))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/admin/announcements/', {
        ...formData,
        published_at: formData.is_published ? new Date().toISOString() : null,
      });
      setAnnouncements([res.data, ...announcements]);
      setShowForm(false);
      setFormData({ title: '', content: '', priority: 'normal', target_audience: 'all', is_published: true });
      toast.success('Announcement created!');
    } catch {
      toast.error('Failed to create announcement.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/admin/announcements/${id}/`);
      setAnnouncements(announcements.filter(a => a.id !== id));
      toast.success('Deleted.');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
        {canAdd(PERMISSIONS.ADD_ANNOUNCEMENTS) && (
          <button onClick={() => setShowForm(!showForm)}
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
            {showForm ? 'Cancel' : 'New Announcement'}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border p-6 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Title</label>
            <input type="text" required value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Content</label>
            <textarea rows={4} required value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Priority</label>
              <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Audience</label>
              <select value={formData.target_audience} onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                <option value="all">All Members</option>
                <option value="active">Active Only</option>
                <option value="admin">Admin Only</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="rounded border-gray-300 text-[#0B5D1E] focus:ring-[#0B5D1E]" />
                <span className="text-sm font-medium text-gray-700">Publish immediately</span>
              </label>
            </div>
          </div>
          <button type="submit" className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold">Create</button>
        </form>
      )}

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E] mx-auto"></div></div>
        ) : announcements.length === 0 ? (
          <div className="bg-white rounded-xl border p-8 text-center text-gray-500">No announcements yet.</div>
        ) : announcements.map((a) => (
          <div key={a.id} className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-semibold text-gray-900">{a.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                    a.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    a.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                  }`}>{a.priority}</span>
                  {a.is_published ? (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Published</span>
                  ) : (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Draft</span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2">{a.content}</p>
                <p className="text-xs text-gray-500">
                  Audience: {a.target_audience} | Created: {new Date(a.created_at).toLocaleDateString()}
                </p>
              </div>
              {canDelete(PERMISSIONS.DELETE_ANNOUNCEMENTS) && (
                <button onClick={() => handleDelete(a.id)}
                  className="text-red-500 hover:text-red-700 text-sm ml-4">Delete</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
